import { useEffect, useState, useCallback } from "react";
import {
  fetchAdminShipping,
  adminCreateShipping,
  adminUpdateShipping,
  adminDeleteShipping,
} from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, Trash2, Truck, Save } from "lucide-react";

const ROLES = [
  "customer",
  "subscriber",
  "apprentice",
  "professional",
  "spa",
  "dist",
  "Ambassador",
  "none",
];

const fmtUSD = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });

/**
 * Admin Shipping Rates tab.
 *
 * Lists & edits rows in the Airtable `Shipping` table:
 *   Shipping_tittle, Shipping_fee, Shipping_cost, user_role,
 *   Handling_fee, Cost_per_weight, Cost_per_item, sub_total, cue.
 *
 * Admin can add, edit-in-place, and delete rows.
 */
export default function ShippingTab({ callerUserRecordId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({}); // recId → pending field map
  const [savingId, setSavingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState({
    Shipping_tittle: "",
    Shipping_fee: 0,
    Shipping_cost: 0,
    user_role: "customer",
    Handling_fee: 0,
  });

  const load = useCallback(async () => {
    if (!callerUserRecordId) return;
    setLoading(true);
    try {
      const res = await fetchAdminShipping({ callerUserRecordId });
      setRows(res?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  }, [callerUserRecordId]);

  useEffect(() => {
    load();
  }, [load]);

  function patch(id, k, v) {
    setEdits((cur) => ({ ...cur, [id]: { ...(cur[id] || {}), [k]: v } }));
  }

  async function save(id) {
    const fields = edits[id];
    if (!fields || Object.keys(fields).length === 0) return;
    setSavingId(id);
    try {
      const res = await adminUpdateShipping({
        callerUserRecordId,
        recordId: id,
        fields,
      });
      setRows((cur) => cur.map((r) => (r.id === id ? res.row : r)));
      setEdits((cur) => {
        const { [id]: _drop, ...rest } = cur;
        return rest;
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingId(null);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this shipping rate?")) return;
    try {
      await adminDeleteShipping({ callerUserRecordId, recordId: id });
      setRows((cur) => cur.filter((r) => r.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }

  async function addNew() {
    if (!newRow.Shipping_tittle.trim()) {
      toast.error("Title is required");
      return;
    }
    setAdding(true);
    try {
      const res = await adminCreateShipping({
        callerUserRecordId,
        fields: {
          Shipping_tittle: newRow.Shipping_tittle.trim(),
          Shipping_fee: Number(newRow.Shipping_fee || 0),
          Shipping_cost: Number(newRow.Shipping_cost || 0),
          user_role: newRow.user_role,
          Handling_fee: Number(newRow.Handling_fee || 0),
        },
      });
      setRows((cur) => [res.row, ...cur]);
      setNewRow({
        Shipping_tittle: "",
        Shipping_fee: 0,
        Shipping_cost: 0,
        user_role: "customer",
        Handling_fee: 0,
      });
      toast.success("Rate added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setAdding(false);
    }
  }

  return (
    <div data-testid="shipping-tab">
      <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-900 leading-relaxed">
        <b>How shipping is calculated:</b> total ={" "}
        <span className="font-mono">Fee&nbsp;+&nbsp;(Per-oz × cart weight)</span>{" "}
        — matched on the buyer&apos;s <b>Role</b>. Rates are scoped to your{" "}
        <b>seller_id</b>; iOS &amp; chatbot share these rows.
      </div>

      {/* Add new */}
      <div className="rounded-2xl border border-zinc-200 bg-white p-3 mb-4">
        <div className="text-[11px] font-bold uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-1.5">
          <Plus size={12} /> Add Shipping Rate
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
          <Input
            placeholder="Title (UPS Ground)"
            value={newRow.Shipping_tittle}
            onChange={(e) =>
              setNewRow((s) => ({ ...s, Shipping_tittle: e.target.value }))
            }
            className="h-9 col-span-2"
            data-testid="shipping-new-title"
          />
          <select
            value={newRow.user_role}
            onChange={(e) =>
              setNewRow((s) => ({ ...s, user_role: e.target.value }))
            }
            className="h-9 rounded-md border border-zinc-200 px-2 text-sm"
            data-testid="shipping-new-role"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <Input
            type="number"
            step="0.01"
            placeholder="Fee"
            value={newRow.Shipping_fee}
            onChange={(e) =>
              setNewRow((s) => ({ ...s, Shipping_fee: e.target.value }))
            }
            className="h-9"
          />
          <Input
            type="number"
            step="0.01"
            placeholder="Cost"
            value={newRow.Shipping_cost}
            onChange={(e) =>
              setNewRow((s) => ({ ...s, Shipping_cost: e.target.value }))
            }
            className="h-9"
          />
          <button
            onClick={addNew}
            disabled={adding}
            className="h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5"
            data-testid="shipping-add"
          >
            {adding ? (
              <Loader2 className="animate-spin" size={13} />
            ) : (
              <>
                <Plus size={13} /> Add
              </>
            )}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center">
          <Loader2 className="animate-spin text-zinc-400" size={20} />
        </div>
      ) : rows.length === 0 ? (
        <div className="py-10 text-center text-sm text-zinc-500">
          <Truck className="mx-auto mb-2 text-zinc-300" size={28} />
          No shipping rates yet.
        </div>
      ) : (
        <div className="rounded-2xl border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[11px] uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">Title</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-right px-3 py-2">Flat&nbsp;fee</th>
                <th className="text-right px-3 py-2">Per&nbsp;oz</th>
                <th className="text-right px-3 py-2">Handling</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.map((r) => {
                const e = edits[r.id] || {};
                const dirty = Object.keys(e).length > 0;
                const v = (k, fallback) =>
                  e[k] !== undefined ? e[k] : fallback;
                return (
                  <tr key={r.id} data-testid={`shipping-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <Input
                        value={v("Shipping_tittle", r.title)}
                        onChange={(ev) =>
                          patch(r.id, "Shipping_tittle", ev.target.value)
                        }
                        className="h-8 text-sm"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={v("user_role", r.user_role || "")}
                        onChange={(ev) =>
                          patch(r.id, "user_role", ev.target.value)
                        }
                        className="h-8 rounded-md border border-zinc-200 px-2 text-sm w-full"
                      >
                        {[...ROLES, r.user_role]
                          .filter(Boolean)
                          .filter((x, i, a) => a.indexOf(x) === i)
                          .map((x) => (
                            <option key={x} value={x}>
                              {x}
                            </option>
                          ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={v("Shipping_fee", r.fee)}
                        onChange={(ev) =>
                          patch(r.id, "Shipping_fee", Number(ev.target.value))
                        }
                        className="h-8 text-sm text-right"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={v("Shipping_cost", r.cost)}
                        onChange={(ev) =>
                          patch(r.id, "Shipping_cost", Number(ev.target.value))
                        }
                        className="h-8 text-sm text-right"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={v("Handling_fee", r.handling_fee)}
                        onChange={(ev) =>
                          patch(r.id, "Handling_fee", Number(ev.target.value))
                        }
                        className="h-8 text-sm text-right"
                      />
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {dirty && (
                        <button
                          onClick={() => save(r.id)}
                          disabled={savingId === r.id}
                          className="mr-1 inline-flex items-center justify-center w-7 h-7 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                          data-testid={`shipping-save-${r.id}`}
                        >
                          {savingId === r.id ? (
                            <Loader2 className="animate-spin" size={12} />
                          ) : (
                            <Save size={12} />
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => remove(r.id)}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-rose-500 hover:bg-rose-50"
                        data-testid={`shipping-delete-${r.id}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
